import { ReactElement } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { PaymentInterval } from '@novaheal/types';
import { PromoCode } from 'types/PromoCode';
import { ReferralInfoDto } from 'hooks/api/backend/user/useReferral';
import { SubscriptionPrice } from 'hooks/api/backend/subscription/useSubscriptionPrices';
import { SubscriptionButton } from './SubscriptionButton';

type Props = {
  activeButton: PaymentInterval;
  onClick: (subscription: PaymentInterval) => void;
  onSubscribe: () => void;
  isLoading: boolean;
  subscriptionPrices: SubscriptionPrice[];
  promoCode?: PromoCode;
  referralInfos?: ReferralInfoDto;
};

const PaymentIntervalToPackageType = (paymentInterval: PaymentInterval): 'yearly' | 'monthly' => {
  switch (paymentInterval) {
    case PaymentInterval.YEARLY:
      return 'yearly';
    case PaymentInterval.MONTHLY:
      return 'monthly';
    default:
      return 'monthly';
  }
};

const calculatePriceWithDiscount = (
  basePrice: number,
  discount: PromoCode['value'] | undefined,
  packageType: PaymentInterval,
  promoCode: PromoCode | undefined
): number => {
  // early out, if a pkg limitation is set and the current pkg does not match
  if (promoCode?.package && promoCode.package !== PaymentIntervalToPackageType(packageType))
    return basePrice;
  if (!discount) return basePrice;

  if (discount.unit === 'percent') {
    return Math.max(basePrice - (basePrice * discount.amount) / 100, 0);
  }

  return Math.max(basePrice - discount.amount, 0);
};

export const SubscriptionButtons = ({
  activeButton,
  onClick,
  onSubscribe,
  isLoading,
  subscriptionPrices,
  promoCode,
  referralInfos,
}: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);

  const getSubscriptionButtonConfig = (
    subscriptionPrice: SubscriptionPrice
  ): {
    interval: PaymentInterval;
    duration: string;
    additionalInfo: string | undefined;
    pricePerMonth: string;
    price: string | undefined;
    oldMonthlyPricing: string | undefined;
    savedPercentage: number | undefined;
    hasPromoCode: boolean;
    hasReferral: boolean;
  } => {
    const variant = subscriptionPrice.interval;

    let duration;
    let additionalInfo;
    let pricePerMonth;
    let price;
    let oldMonthlyPricing;
    let savedPercentage;
    let hasPromoCode = false;
    let hasReferral = false;

    switch (variant) {
      case PaymentInterval.MONTHLY: {
        const monthlyPrice = Number(Number(subscriptionPrice.price).toFixed(2));
        const monthlyPriceWithDiscount = calculatePriceWithDiscount(
          monthlyPrice,
          promoCode?.value,
          variant,
          promoCode
        );
        const monthlySavedPercentage = Math.floor(
          ((monthlyPrice - monthlyPriceWithDiscount) / monthlyPrice) * 100
        );

        duration = '1 Monat';
        pricePerMonth = `${monthlyPriceWithDiscount.toFixed(2)} €/Monat`;
        // Promo code is active if it exists and applies to this package (no package restriction or matches 'monthly')
        hasPromoCode = !!promoCode && (!promoCode.package || promoCode.package === 'monthly');
        // Only show discount/strikethrough if promo code is active
        if (hasPromoCode) {
          oldMonthlyPricing = `${monthlyPrice} €/Monat`;
          savedPercentage = monthlySavedPercentage > 0 ? monthlySavedPercentage : undefined;
        }
        break;
      }
      case PaymentInterval.YEARLY: {
        const yearlyPriceWithDiscount = calculatePriceWithDiscount(
          subscriptionPrice.price,
          promoCode?.value ||
            (referralInfos
              ? {
                  amount: referralInfos.discount.value,
                  unit: referralInfos.discount.unit,
                }
              : undefined),
          variant,
          promoCode
        );
        const yearlyPricePerMonth = Math.floor((subscriptionPrice.price / 12) * 100) / 100;
        const yearlyPricePerMonthDiscounted =
          Math.floor((yearlyPriceWithDiscount / 12) * 100) / 100;
        const yearlySavedPercentage = Math.floor(
          ((yearlyPricePerMonth - yearlyPricePerMonthDiscounted) / yearlyPricePerMonth) * 100
        );

        duration = '12 Monate';
        additionalInfo = 'Zahlung jährlich. Jederzeit kündbar.';
        // Promo code is active if it exists and applies to this package (no package restriction or matches 'yearly')
        hasPromoCode = !!promoCode && (!promoCode.package || promoCode.package === 'yearly');
        hasReferral = !!referralInfos;

        // Use discounted price per month (includes both promo code and referral discounts)
        // But only show strikethrough/percentage if promo code is active
        const hasAnyDiscount = hasPromoCode || hasReferral;
        pricePerMonth = hasAnyDiscount
          ? `${yearlyPricePerMonthDiscounted.toFixed(2)} €/Monat`
          : `${yearlyPricePerMonth.toFixed(2)} €/Monat`;
        price = `${yearlyPriceWithDiscount.toFixed(2)} €`;

        // Only show discount/strikethrough if promo code is active (not for referrals)
        if (hasPromoCode) {
          oldMonthlyPricing = `${yearlyPricePerMonth} €/Monat`;
          savedPercentage = yearlySavedPercentage > 0 ? yearlySavedPercentage : undefined;
        }
        break;
      }
      case PaymentInterval.BIWEEKLY:
        duration = '2 Tage';
        additionalInfo = 'Dies ist ein Testprodukt. Nur zum internen Gebrauch.';
        pricePerMonth = `${Number(2 * subscriptionPrice.price).toFixed(2)} €/Monat`;
        price = `${subscriptionPrice.price} €`;
        break;
      default:
        duration = 'Unknown duration';
        pricePerMonth = '';
    }

    return {
      interval: variant,
      duration,
      additionalInfo,
      pricePerMonth,
      price,
      oldMonthlyPricing,
      savedPercentage,
      hasPromoCode,
      hasReferral,
    };
  };

  const subscriptionConfigs = subscriptionPrices
    ? subscriptionPrices.map(getSubscriptionButtonConfig)
    : [];

  return (
    <Flex gap="25px" width="100%" flexDirection="column">
      <Flex gap="15px" width="100%" flexDirection="column">
        {isDesktop && <Text color="text.primary">Wähle deinen Plan:</Text>}
        {subscriptionConfigs.map((config) => (
          <SubscriptionButton
            key={config.interval}
            isActive={config.interval === activeButton}
            onClick={() => onClick(config.interval)}
            duration={config.duration}
            pricePerMonth={config.pricePerMonth}
            oldMonthlyPricing={config.oldMonthlyPricing}
            additionalInfo={config.additionalInfo}
            savedPercentage={config.savedPercentage}
            hasPromoCode={config.hasPromoCode}
            hasReferral={config.hasReferral}
            price={config.price}
          />
        ))}
      </Flex>
      {isDesktop && (
        <Button
          variant="primary"
          width="220px"
          alignSelf="center"
          onClick={onSubscribe}
          loading={isLoading}
        >
          Abonnieren
        </Button>
      )}
    </Flex>
  );
};
